import React, { useState, useEffect, useRef } from "react";
import "./TrustStatistics.css";
import { useNavigate } from "react-router-dom";

// Custom hook for counter animation
const useCountAnimation = (end, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let startTime;
    let animationId;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [end, duration, shouldStart]);

  return count;
};

// Component for animated statistic number
const AnimatedStatistic = ({ number, label, shouldAnimate }) => {
  // Parse the number (handle k+ and commas)
  const parseNumber = (numStr) => {
    const cleanNum = numStr.replace(/[,+]/g, "");
    if (numStr.includes("k")) {
      return parseInt(cleanNum) * 1000;
    }
    return parseInt(cleanNum);
  };

  const formatNumber = (num, original) => {
    if (original.includes("k")) {
      const thousands = Math.floor(num / 1000);
      return `${thousands}k+`;
    }
    if (original.includes(",")) {
      return num.toLocaleString() + "+";
    }
    return num.toString() + "+";
  };

  const targetValue = parseNumber(number);
  const animatedValue = useCountAnimation(targetValue, 2000, shouldAnimate);

  return (
    <div className="analytics-statistic-container">
      {/* Large Number */}
      <div className="analytics-statistic-number">
        {shouldAnimate ? formatNumber(animatedValue, number) : number}
      </div>

      {/* Label */}
      <div className="analytics-statistic-label">
        {label.split(" ").map((word, wordIndex) => (
          <span key={wordIndex}>
            {word}
            {wordIndex < label.split(" ").length - 1 && (
              <>
                {" "}
                <br className="analytics-breakpoint-hidden" />
              </>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

const TrustStatistics = () => {
  const navigate = useNavigate();

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const statistics = [
    {
      number: "15k+",
      label: "Managers Rated",
    },
    {
      number: "20k+",
      label: "Anonymous Reviews",
    },
    {
      number: "100+",
      label: "Companies Reviewed",
    },
  ];

  // Intersection Observer to trigger animation when component comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.3, // Trigger when 30% of the component is visible
        rootMargin: "0px 0px -100px 0px", // Trigger slightly before fully visible
      }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="analytics-section-container">
      <div className="analytics-content-container">
        {/* Section Title */}
        <h2 className="analytics-section-title">
          Why Engineers Trust Rate My Manager
        </h2>

        {/* Statistics Grid */}
        <div className="analytics-statistics-grid">
          {statistics.map((stat, index) => (
            <AnimatedStatistic
              key={index}
              number={stat.number}
              label={stat.label}
              shouldAnimate={isVisible}
            />
          ))}
        </div>

        {/* Rate Now Button */}
        <button
          className="analytics-rate-button"
          onClick={() => {
            navigate("/add-review");
          }}
        >
          Rate Now
        </button>
      </div>
    </section>
  );
};

export default TrustStatistics;
